import React, { useState } from "react";

const ChatMessage = ({ message, isTyping, messageIndex, conversationId }) => {
  const { role, content, timestamp, id } = message;

  const isUser = role === "human";
  const [feedback, setFeedback] = useState(
    message?.feedback == "" ? null : message?.feedback
  ); // 'up', 'down', or null
  const [feedbackSending, setFeedbackSending] = useState(false);

  const formatTime = (timestamp) => {
    if (!timestamp) return "";

    const messageDate = new Date(timestamp);
    const now = new Date();

    // Reset time parts for date comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDay = new Date(
      messageDate.getFullYear(),
      messageDate.getMonth(),
      messageDate.getDate()
    );

    // Format time
    const timeString = messageDate
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    // Check if today
    if (messageDay.getTime() === today.getTime()) {
      return timeString;
    }

    // Check if yesterday
    if (messageDay.getTime() === yesterday.getTime()) {
      return `Yesterday, ${timeString}`;
    }

    // Older than yesterday - show full date
    const dayName = messageDate.toLocaleDateString("en-US", {
      weekday: "long",
    });
    const day = messageDate.getDate();
    const month = messageDate.toLocaleDateString("en-US", { month: "long" });
    const year = messageDate.getFullYear();

    // Add ordinal suffix to day
    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${dayName}, ${getOrdinal(day)} ${month} ${year}`;
  };

  const handleFeedback = async (type) => {
    if (feedbackSending) return;

    // Toggle feedback - if clicking the same type, remove feedback
    const newFeedback = feedback === type ? null : type;
    setFeedback(newFeedback);
    setFeedbackSending(true);
    try {
      const url =
        process.env.REACT_APP_RESPONSE_FEEDBACK_URL || "/api/feedback";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationId: conversationId || "unknown",
          messageId: id,
          feedback: newFeedback, // null means removing feedback
          content: content,
          timestamp: new Date().toISOString(),
        }),
      });

      // if (response.ok) {

      // }
    } catch (error) {
      console.error("Error sending feedback:", error);
    } finally {
      setFeedbackSending(false);
    }
  };

  // Determine if content is short (roughly one line)
  const isShortContent = !isUser && content && content.length < 100;

  if (isTyping) {
    return (
      <div className="flex justify-start animate-fadeIn mb-4">
        <div className="bg-purple-500/20 backdrop-blur-md border border-purple-500/30 text-purple-300 rounded-2xl px-4 py-3 shadow-lg">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-gradient-to-br from-blue-400 to-purple-600 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">B</span>
            </div>
            <div className="flex space-x-1">
              <span className="typing-dot w-2 h-2 bg-purple-400 rounded-full"></span>
              <span className="typing-dot w-2 h-2 bg-purple-400 rounded-full"></span>
              <span className="typing-dot w-2 h-2 bg-purple-400 rounded-full"></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex ${
        isUser ? "justify-end" : "justify-start"
      } animate-slideUp mb-4`}
    >
      <div
        className={`${
          isUser
            ? "max-w-lg"
            : isShortContent
            ? "max-w-fit"
            : "max-w-5xl w-full"
        }`}
      >
        <div
          className={`
            rounded-2xl px-4 py-3 backdrop-blur-md border shadow-lg
            transition-all duration-300 hover:scale-[1.01]
            ${
              isUser
                ? "bg-blue-500/20 text-blue-100 border-blue-500/30 shadow-blue-500/20 hover:shadow-blue-500/40"
                : "bg-purple-500/20 text-purple-100 border-purple-500/30 shadow-purple-500/20 hover:shadow-purple-500/40"
            }
            ${!isUser && isShortContent ? "inline-block" : ""}
          `}
        >
          <div className="flex items-center space-x-2 mb-1">
            {!isUser && (
              <div className="w-5 h-5 bg-gradient-to-br from-blue-400 to-purple-600 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">B</span>
              </div>
            )}
            <p className="text-sm font-medium opacity-70">
              {isUser ? "You" : "BSA Assist"}
            </p>
          </div>
          <p className="whitespace-pre-wrap break-words">{content}</p>

          {/* Feedback buttons for AI messages */}
          {!isUser && (
            <div className="flex items-center space-x-2 mt-3 pt-2 border-t border-purple-500/20">
              <button
                onClick={() => handleFeedback("up")}
                disabled={feedbackSending}
                className={`p-1.5 rounded-lg transition-all duration-300 ${
                  feedback === "up"
                    ? "bg-green-500/30 text-green-400"
                    : "hover:bg-white/10 text-gray-400 hover:text-green-400"
                } ${feedbackSending ? "opacity-50 cursor-not-allowed" : ""}`}
                title={
                  feedback === "up" ? "Remove feedback" : "Helpful response"
                }
              >
                {feedback === "up" ? (
                  // Filled thumbs up
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2v11zm19.83-7.12c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66-.23-.45-.52-.86-.88-1.22L14 2 7.59 8.41C7.21 8.79 7 9.3 7 9.83v7.84C7 18.95 8.05 20 9.34 20h8.11c.7 0 1.36-.37 1.72-.97l2.66-6.15z" />
                  </svg>
                ) : (
                  // Outline thumbs up
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                    />
                  </svg>
                )}
              </button>

              <button
                onClick={() => handleFeedback("down")}
                disabled={feedbackSending}
                className={`p-1.5 rounded-lg transition-all duration-300 ${
                  feedback === "down"
                    ? "bg-red-500/30 text-red-400"
                    : "hover:bg-white/10 text-gray-400 hover:text-red-400"
                } ${feedbackSending ? "opacity-50 cursor-not-allowed" : ""}`}
                title={feedback === "down" ? "Remove feedback" : "Not helpful"}
              >
                {feedback === "down" ? (
                  // Filled thumbs down
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M22 4h-2c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h2V4zM2.17 11.12c-.11.25-.17.52-.17.8V13c0 1.1.9 2 2 2h5.5l-.92 4.65c-.05.22-.02.46.08.66.23.45.52.86.88 1.22L10 22l6.41-6.41c.38-.38.59-.89.59-1.42V6.34C17 5.05 15.95 4 14.66 4h-8.1c-.71 0-1.36.37-1.72.97l-2.67 6.15z" />
                  </svg>
                ) : (
                  // Outline thumbs down
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
                    />
                  </svg>
                )}
              </button>

              {feedback && (
                <span className="text-xs text-gray-500 ml-2 animate-fadeIn">
                  {feedback === "up"
                    ? "Thanks for your feedback!"
                    : "We'll improve this response"}
                </span>
              )}
            </div>
          )}
        </div>
        {timestamp && (
          <p
            className={`text-xs text-gray-400 mt-1 px-2 ${
              isUser ? "text-right" : "text-left"
            }`}
          >
            {formatTime(timestamp)}
          </p>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
